import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardContent, Box, Typography } from '@mui/material';
import { brand, clickableCardSx } from '../../theme';

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  /** Accent color for the icon tile (defaults to neutral slate; copper is for actions). */
  color?: string;
  /** Optional small caption under the label (e.g. "this month"). */
  caption?: string;
  /** Makes the whole card a link, e.g. to the list behind the number. */
  to?: string;
}

/**
 * Shared stat card — flat white card, tinted icon tile, big value.
 * Replaces the per-page pastel-gradient cards so every screen's metrics
 * read the same way.
 */
const StatCard: React.FC<StatCardProps> = ({ icon, label, value, color = brand.slate, caption, to }) => (
  <Card
    {...(to ? { component: RouterLink, to } : {})}
    sx={{
      height: '100%',
      boxShadow: 'none',
      display: 'block',
      color: 'inherit',
      textDecoration: 'none',
      ...(to ? clickableCardSx : {}),
    }}
  >
    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2.25, p: 2.5, '&:last-child': { pb: 2.5 } }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: `${color}12`,
          color,
          flexShrink: 0
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h4" sx={{ lineHeight: 1.15, mb: 0.25 }}>
          {value}
        </Typography>
        {/* Wraps rather than truncating: two cards across a phone are narrow. */}
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
    </CardContent>
  </Card>
);

export default StatCard;
